import React, { useEffect, useMemo, useState } from 'react';
import { Key, Plus } from 'lucide-react';
import PricingTab from '../../../components/credits/PricingTab';
import {
  putAiModel, putAiProvider, rubyAiErrorText,
  type AiModelDto, type AiProviderDto,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import { RubyLoader } from '../../../components/RubyLoader';
import { DeveloperAiSection } from './DeveloperAiSection';
import {
  USED_FOR_LABELS, USED_FOR_TONE, billsBy, normalizeUsedFor, vendorPriceLabel, isPriced,
} from '../rubyAi/catalogModel';
import { AI_MODEL_USED_FOR, formatRubyTokens, liveTokensPerCredit } from '../rubyAi/units';
import { ModelEditModal, ProviderEditModal, ProviderKeyModal } from '../rubyAi/ProviderModals';
import CapabilityGrid from '../rubyAi/CapabilityGrid';
import ExchangeRates from '../rubyAi/ExchangeRates';
import { useRubyAiCatalog } from '../rubyAi/useRubyAi';
import { Badge, Btn, Card, CardHeader, ErrorBanner, Mono, Toggle } from '../rubyAi/ui';

const CHIP_TONE: Record<string, 'ruby' | 'orange' | 'blue' | 'teal' | 'stone'> = USED_FOR_TONE as any;

/**
 * Providers & Models (mockup board 1) — PROVIDER# and MODEL# registry rows.
 *
 * Credentials are SSM SecureString parameters: this surface shows the PATH and
 * at most a four-character hint, NEVER a decrypted value (spec §9.2); "Update
 * key" is write-only. Each model says what it can be "used for" — Ruby AI
 * Actions only offer models whose used-for matches — and "Bills by" is
 * derived from the vendor's own billing unit (the unitPrices keys set).
 * The legacy per-API pricing panel and its drift approval queue are embedded
 * verbatim below, marked data-legacy-pricing.
 */
const ProvidersModelsTab: React.FC<AiTabProps> = ({
  registry, registryError, reloadRegistry, onHeaderStats, showToast,
}) => {
  const [saving, setSaving] = useState<string | null>(null);
  const [editProvider, setEditProvider] = useState<AiProviderDto | null | 'new'>(null);
  const [keyProvider, setKeyProvider] = useState<AiProviderDto | null>(null);
  const [editModel, setEditModel] = useState<AiModelDto | null | 'new'>(null);
  // Key updates made in this session (the key itself is never kept).
  const [keyUpdated, setKeyUpdated] = useState<Record<string, string>>({});
  // The capability grid and exchange-rate card need actions/vibes (and
  // server-computed `usedBy`) that only the ext-stack catalogue carries —
  // the plain registry fetch above does not enrich for either.
  const { catalog: rubyCatalog, error: rubyCatalogError } = useRubyAiCatalog();

  const creditUsd = registry?.config?.creditUnitPriceUsd ?? 0.01;

  useEffect(() => {
    const providers = registry?.providers ?? [];
    const models = registry?.models ?? [];
    onHeaderStats([
      { label: 'Providers', value: `${providers.filter((p) => p.enabled).length}/${providers.length}`, tone: 'emerald' },
      { label: 'Models', value: `${models.filter((m) => m.enabled).length}/${models.length}`, tone: 'blue' },
      { label: `${formatRubyTokens(liveTokensPerCredit())} Ruby Tokens`, value: `$${creditUsd}`, tone: 'white' },
    ]);
  }, [registry, onHeaderStats, creditUsd]);

  const providerLabel = useMemo(() => {
    const map = new Map((registry?.providers ?? []).map((p) => [p.providerId, p.label]));
    return (id: string) => map.get(id) ?? id;
  }, [registry]);

  const toggleProvider = async (p: AiProviderDto, enabled: boolean) => {
    setSaving(`p:${p.providerId}`);
    try {
      await putAiProvider(p.providerId, { enabled });
      showToast(`${p.label} ${enabled ? 'enabled' : 'disabled'}`, 'success');
      await reloadRegistry();
    } catch (err) {
      showToast(rubyAiErrorText(err, 'Failed to update the provider'), 'error');
    } finally {
      setSaving(null);
    }
  };

  const toggleModel = async (m: AiModelDto, enabled: boolean) => {
    setSaving(`m:${m.providerId}#${m.modelId}`);
    try {
      await putAiModel(m.providerId, m.modelId, { enabled });
      showToast(`${m.label} ${enabled ? 'enabled' : 'disabled'}`, 'success');
      await reloadRegistry();
    } catch (err) {
      showToast(rubyAiErrorText(err, 'Failed to update the model'), 'error');
    } finally {
      setSaving(null);
    }
  };

  if (registryError) return <ErrorBanner title="Failed to load the AI registry" detail={registryError} />;
  if (!registry) return <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />;

  const models = [...registry.models].sort((a, b) =>
    providerLabel(a.providerId).localeCompare(providerLabel(b.providerId)) || a.label.localeCompare(b.label));

  return (
    <>
      {/* Developer AI: the issue console's analysis + fix models (ROLE#dev-analysis / dev-fix). */}
      <DeveloperAiSection registry={registry} showToast={showToast} />

      {/* PROVIDER# rows */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[15px] font-semibold text-stone-900">Providers</h2>
            <p className="text-[12px] text-stone-600">Keys live in SSM. This page shows the path only — a key is never displayed after it is saved.</p>
          </div>
          <Btn kind="primary" onClick={() => setEditProvider('new')}><Plus size={16} /> Add provider</Btn>
        </div>
        {registry.providers.length === 0 ? (
          <Card className="p-8 text-center text-sm text-stone-600">No providers registered yet.</Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {registry.providers.map((p) => {
              const updated = keyUpdated[p.providerId];
              return (
                <Card key={p.providerId} className="p-4 flex flex-col gap-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-stone-900 truncate" title={p.label}>{p.label}</p>
                      <p className="text-[11px] text-stone-500 flex items-center gap-1.5">
                        {p.vendor}
                        <Badge tone={p.enabled ? 'green' : 'stone'}>{p.enabled ? 'Enabled' : 'Disabled'}</Badge>
                      </p>
                    </div>
                    <Toggle on={p.enabled} busy={saving === `p:${p.providerId}`} label={`${p.enabled ? 'Disable' : 'Enable'} ${p.label}`}
                      onChange={(v) => void toggleProvider(p, v)} />
                  </div>
                  <p className="font-mono text-[12px] text-stone-600 break-all flex gap-1">
                    <Key size={12} className="flex-shrink-0 mt-0.5" />{p.credentialsRef}
                  </p>
                  <p className="text-[12px] text-stone-600">
                    {p.authType === 'serviceAccount' ? 'Service account' : p.authType === 'query' ? 'Query key' : 'API key'}
                    {' · '}
                    {updated
                      ? <span className="text-emerald-700">key updated {new Date(updated).toLocaleString()}</span>
                      : p.credentialHint ? <>stored {p.credentialHint}</> : 'key held in SSM'}
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-auto">
                    <Btn className="!px-2 whitespace-nowrap" onClick={() => setEditProvider(p)}>Edit</Btn>
                    <Btn kind="soft" className="!px-2 whitespace-nowrap" onClick={() => setKeyProvider(p)}>Update key</Btn>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* MODEL# rows */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[15px] font-semibold text-stone-900">Models</h2>
            <p className="text-[12px] text-stone-600">Each model states what it can be used for. Ruby AI Actions only offer models whose “Used for” matches.</p>
          </div>
          <Btn kind="primary" onClick={() => setEditModel('new')} disabled={registry.providers.length === 0}><Plus size={16} /> Add model</Btn>
        </div>
        <Card className="overflow-hidden">
          {models.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-600">No models registered yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-600">
                    <th className="text-left font-semibold px-4 py-3">Model</th>
                    <th className="text-left font-semibold px-3 py-3">Provider</th>
                    <th className="text-left font-semibold px-3 py-3">Used for</th>
                    <th className="text-left font-semibold px-3 py-3">Bills by</th>
                    <th className="text-left font-semibold px-3 py-3">Vendor price</th>
                    <th className="text-left font-semibold px-3 py-3">On</th>
                    <th className="px-3 py-3"><span className="sr-only">Edit</span></th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((m) => {
                    const { usedFor, other } = normalizeUsedFor(m.capabilities, AI_MODEL_USED_FOR);
                    const key = `m:${m.providerId}#${m.modelId}`;
                    return (
                      <tr key={key} className="border-b border-stone-100 last:border-0 align-middle">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-stone-900">{m.label}</p>
                          <Mono className="text-stone-500">{m.modelId}</Mono>
                        </td>
                        <td className="px-3 py-3 text-stone-700">{providerLabel(m.providerId)}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1.5">
                            {usedFor.map((u) => <Badge key={u} square tone={CHIP_TONE[u] ?? 'stone'}>{USED_FOR_LABELS[u] ?? u}</Badge>)}
                            {other.map((u) => <Badge key={u} square tone="stone">{u}</Badge>)}
                            {!usedFor.length && !other.length && <span className="text-[12px] text-amber-800">Not set</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-stone-700">{billsBy(m.unitPrices)}</td>
                        <td className="px-3 py-3">
                          <Mono className={isPriced(m) ? 'text-stone-800' : 'text-amber-800'}>{vendorPriceLabel(m.unitPrices)}</Mono>
                          {typeof m.driftPercent === 'number' && m.driftPercent !== 0 && (
                            <span className="block text-[11px] text-amber-800">drift {m.driftPercent > 0 ? '+' : ''}{m.driftPercent.toFixed(1)}%</span>
                          )}
                        </td>
                        <td className="px-3 py-1">
                          <Toggle on={m.enabled} busy={saving === key} label={`${m.enabled ? 'Disable' : 'Enable'} ${m.label}`}
                            onChange={(v) => void toggleModel(m, v)} />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Btn onClick={() => setEditModel(m)}>Edit</Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <p className="text-[12px] text-stone-600">
          “Bills by” comes from the vendor’s own billing unit, so our price is always cost × multiplier on the same unit the vendor charges us.
        </p>
      </section>

      {/* Exactly which model makes each picture (Task 15) */}
      <section className="space-y-3">
        <div>
          <h2 className="text-[15px] font-semibold text-stone-900">Exactly which model makes each picture</h2>
          <p className="text-[12px] text-stone-600">
            The ambiguous “Image” chip is retired — a model draws, edits, finds or reads images, and can be more than one.
            “Used by” shows which action step, vibe override or role actually routes to it today.
          </p>
        </div>
        {rubyCatalogError && !rubyCatalog ? (
          <ErrorBanner title="Failed to load actions & vibes for the capability grid" detail={rubyCatalogError} />
        ) : !rubyCatalog ? (
          <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />
        ) : (
          <CapabilityGrid models={rubyCatalog.models} actions={rubyCatalog.actions} vibes={rubyCatalog.vibes} />
        )}
      </section>

      {/* Every model has its own rate into Ruby Tokens (Task 15) */}
      <section className="space-y-3">
        <div>
          <h2 className="text-[15px] font-semibold text-stone-900">Every model has its own rate into Ruby Tokens</h2>
          <p className="text-[12px] text-stone-600">
            1 Ruby Token = $0.00001. “1 unit charged as” is cost × the multiplier on the action that uses this model as its primary — a model with no primary action shows the 1.5 default, greyed.
          </p>
        </div>
        {rubyCatalogError && !rubyCatalog ? (
          <ErrorBanner title="Failed to load models for the exchange-rate card" detail={rubyCatalogError} />
        ) : !rubyCatalog ? (
          <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />
        ) : (
          <ExchangeRates models={rubyCatalog.models} actions={rubyCatalog.actions} vibes={rubyCatalog.vibes} />
        )}
      </section>

      {/* Legacy per-API pricing, drift queue and credit bundles — reused verbatim. */}
      <div data-legacy-pricing>
        <Card className="overflow-hidden">
          <CardHeader title="Pricing sync & pending changes" hint="Drift detected by the daily sync waits for approval here before it changes a price." />
          <div className="p-4"><PricingTab /></div>
        </Card>
      </div>

      <ProviderEditModal
        open={editProvider !== null}
        provider={editProvider === 'new' ? null : editProvider}
        existingIds={registry.providers.map((p) => p.providerId)}
        onClose={() => setEditProvider(null)}
        onSaved={reloadRegistry}
        showToast={showToast}
      />
      <ProviderKeyModal
        open={keyProvider !== null}
        provider={keyProvider}
        onClose={() => setKeyProvider(null)}
        onSaved={(id, at) => setKeyUpdated((k) => ({ ...k, [id]: at }))}
        showToast={showToast}
      />
      <ModelEditModal
        open={editModel !== null}
        model={editModel === 'new' ? null : editModel}
        providers={registry.providers}
        models={registry.models}
        onClose={() => setEditModel(null)}
        onSaved={reloadRegistry}
        showToast={showToast}
      />
    </>
  );
};

export default ProvidersModelsTab;
